import { yupResolver } from '@hookform/resolvers/yup'
import log from 'loglevel'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import AuthApi from 'apis/auth'
import request from 'apis/request'
import { AuthErrorCodes, proxyLogin } from 'commons/firebase-auth'
import Page from 'commons/page'
import Block from 'components/block'
import ButtonV2 from 'components/button-v2'
import { Form, FormControl } from 'components/form'
import { Input } from 'components/input-control'
import { ButtonWrapper } from 'components/layout'
import { PageTitle, Text } from 'components/typography'
import { JWT_TOKEN_KEY_NAME, LOGIN_USER_MODE } from 'constants/common'
import { LoginUserModeEnum } from 'constants/loginusermode'
import { ErrorMsg } from 'constants/message'
import ROUTING_PATH from 'constants/route'
import { useAuthProvider } from 'contexts/auth'
import { getAuth, signOut } from 'firebase/auth'
import { UserRole } from 'schema/user'

interface LoginProp {
  email: string
}

const OwnerLogin = () => {
  const { currentUser, claims } = useAuthProvider()
  const [loading, setLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const handleImpersonation = useCallback(async () => {
    const isImpersonation = searchParams.get('impersonation')
    const accessKey = searchParams.get('key')
    if (isImpersonation && accessKey) {
      try {
        const auth = getAuth()
        await signOut(auth)

        const res = await request<{ token?: string; redirect_url?: string }>({
          method: 'post',
          url: `/agents/impersonation-signin`,
          data: { accessKey },
          requireAuth: false,
        })

        if (res.data.token) {
          const proxyToken = await proxyLogin(res.data.token as string)
          const token = await proxyToken.getIdToken()
          localStorage.setItem(JWT_TOKEN_KEY_NAME, token)
          localStorage.setItem(LOGIN_USER_MODE, LoginUserModeEnum.Impersonation)
          navigate(ROUTING_PATH.B0201)
        } else if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url
        } else {
          throw new Error('予期せぬエラー')
        }
      } catch (e: any) {
        log.error(e)
      }
    }
  }, [searchParams, navigate])

  useEffect(() => {
    handleImpersonation()
  }, [handleImpersonation])

  const schema = yup.object({
    email: yup
      .string()
      .required(ErrorMsg.REQUIRED)
      .email(ErrorMsg.INVALID_EMAIL),
  })

  const form = useForm<LoginProp>({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (currentUser && claims && claims?.role === UserRole.OWNER) {
      navigate(ROUTING_PATH.B0201)
    }
  }, [claims, currentUser, navigate])

  const handleSubmit = useCallback(
    async (data: LoginProp) => {
      try {
        setLoading(true)
        await AuthApi.sendSignInLink(data.email)
          .then(() => {
            window.localStorage.setItem('emailForSignIn', data.email)
            navigate(ROUTING_PATH.OwnerSignInLink)
          })
          .catch((e) => {
            switch (e.code) {
              case AuthErrorCodes.USER_DELETED:
                form.setError('email', {
                  type: 'custom',
                  message: ErrorMsg.NOT_FOUND_EMAIL,
                })
                break
              default:
                throw e
            }
          })
      } catch (e: any) {
        log.error(e)
      }

      setLoading(false)
    },
    [form, navigate]
  )

  return (
    <Page title="ログイン" loading={loading} type="guest" width="400px">
      <PageTitle>売主様用ログインページ</PageTitle>
      <Block style={{ marginBottom: '10px' }}>
        <Form form={form} onSubmit={handleSubmit}>
          <FormControl label="メールアドレス" name="email">
            <Input
              type="email"
              placeholder="abc@example.com"
              maxLength={200}
              aria-label="メールアドレス"
            />
          </FormControl>
          <ButtonWrapper>
            <ButtonV2 size="full" radius="full">
              ログインURLを送信する
            </ButtonV2>
          </ButtonWrapper>
        </Form>
      </Block>
      <Block>
        <Text size="md">
          こちらは、TERASSに所属するエージェントと媒介契約を結んだ売主様向けのログインページです。
        </Text>
        <Text size="md">
          ログインには、エージェントにお伝えしたメールアドレスをご入力ください。
        </Text>
        <Text size="md">
          お問い合わせは、担当エージェントにお伝えください。
        </Text>
      </Block>
    </Page>
  )
}

export default OwnerLogin
